import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import LockIcon from '@mui/icons-material/Lock'
import { useState } from 'react'
import UpgradeDialog from '../UpgradeDialog'

interface ColumnGroupToggleProps {
  visibleGroups: string[]
  onToggleGroup: (group: string) => void
  condensed?: boolean
}

export default function ColumnGroupToggle({
  visibleGroups,
  onToggleGroup,
  condensed = false,
}: ColumnGroupToggleProps) {
  const theme = useTheme()
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false)

  const { canViewBacktestInsights } = useApplicationSuiteContext()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <ToggleButtonGroup
        size="small"
        value={visibleGroups}
        onChange={(_, newGroups) => {
          if (newGroups !== null) {
            // Find the newly selected group (the one that was just clicked)
            const newlySelected = newGroups.find((g: string) => !visibleGroups.includes(g))

            if (newlySelected) {
              // Prevent toggling to "advanced" if user doesn't have access
              if (newlySelected === 'advanced' && !canViewBacktestInsights) {
                return // Don't allow the toggle
              }

              // Single select behavior - switch to the newly selected group
              // First deselect all other groups, then select the new one
              visibleGroups.forEach((group) => {
                if (group !== newlySelected) {
                  onToggleGroup(group)
                }
              })
              // Then select the new group if it's not already selected
              if (!visibleGroups.includes(newlySelected)) {
                onToggleGroup(newlySelected)
              }
            }
          }
        }}
        sx={{
          '& .MuiToggleButton-root': {
            fontSize: condensed ? '0.7rem' : '0.75rem',
            px: condensed ? 1 : 1.5,
            py: condensed ? 0.25 : 0.5,
            border: `1px solid ${theme.palette.divider}`,
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        }}
      >
        <ToggleButton value="essentials">Essentials</ToggleButton>
        <ToggleButton value="basics">Basics</ToggleButton>
        <ToggleButton
          value="advanced"
          onClick={() => {
            if (!canViewBacktestInsights) {
              setUpgradeDialogOpen(true)
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            opacity: !canViewBacktestInsights ? 0.6 : 1,
            cursor: canViewBacktestInsights ? 'pointer' : 'default',
          }}
        >
          Advanced
          {!canViewBacktestInsights && <LockIcon fontSize="small" />}
        </ToggleButton>
      </ToggleButtonGroup>

      <UpgradeDialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        marketplaceSection="membership"
      />
    </Box>
  )
}
